import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import FormatBar from '../format_bar/formatBar';
import './adminPraktiskPriser.css';

// Hjælpefunktion til at normalisere whitespace i HTML-indhold
const normalizeWhitespace = (html) => {
  if (!html) return '';

  // Erstat alle newlines med space
  let normalized = html.replace(/\n/g, ' ');

  // Erstat multiple spaces med et enkelt space
  normalized = normalized.replace(/\s+/g, ' ');

  // Opdeler HTML i dele baseret på tags
  const parts = [];
  let currentIndex = 0;
  const regex = /<[^>]+>/g;
  let match;

  while ((match = regex.exec(normalized)) !== null) {
    // Tekst før tag
    if (match.index > currentIndex) {
      const textBefore = normalized.substring(currentIndex, match.index);
      parts.push(textBefore);
    }

    // Tag'et selv
    parts.push(match[0]);
    currentIndex = match.index + match[0].length;
  }

  // Tekst efter sidste tag
  if (currentIndex < normalized.length) {
    parts.push(normalized.substring(currentIndex));
  }

  // Genopbyg HTML, mens vi sikrer korrekt whitespace
  return parts.map(part => {
    // Hvis det er et tag, returner det som det er
    if (part.startsWith('<') && part.endsWith('>')) {
      return part;
    }
    // Hvis det er tekst (ikke et tag), trim kun leading/trailing whitespace
    return part;
  }).join('');
};

const AdminPraktiskPriser = () => {
  const navigate = useNavigate();
  const textBeforeRef = useRef(null);
  const textAfterRef = useRef(null);

  // Auth state
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Data states
  const [praktiskData, setPraktiskData] = useState({
    title: 'Praktisk & Priser',
    heroImage: '',
    textBefore: '',
    locationImage: '',
    mapEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2249.484931924789!2d12.5903163159283!3d55.68268098053369!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x465253764030803b%3A0xf12e177be3965413!2sSankt%20Ann%C3%A6%20Plads%2012%2C%201250%20K%C3%B8benhavn!5e0!3m2!1sda!2sdk!4v1645541234567!5m2!1sda!2sdk',
    textAfter: ''
  });
  
  const [heroFile, setHeroFile] = useState(null);
  const [locationFile, setLocationFile] = useState(null);
  const [heroPreview, setHeroPreview] = useState(null);
  const [locationPreview, setLocationPreview] = useState(null);
  
  // UI states
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isEditing, setIsEditing] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Format states
  const [textBeforeFormats, setTextBeforeFormats] = useState({
    bold: false,
    italic: false,
    heading: false,
    bullet: false,
    link: false,
    underline: false
  });

  const [textAfterFormats, setTextAfterFormats] = useState({
    bold: false,
    italic: false,
    heading: false,
    bullet: false,
    link: false,
    underline: false
  });

  // Check auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setIsLoading(false);
      if (!user) {
        navigate('/admin/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Load content
  useEffect(() => {
    const fetchPraktiskData = async () => {
      try {
        // Fetch praktisk priser content
        const praktiskDocRef = doc(db, 'content', 'praktiskPriser');
        const praktiskDoc = await getDoc(praktiskDocRef);
        
        if (praktiskDoc.exists()) {
          const data = praktiskDoc.data();
          setPraktiskData({
            title: data.title || 'Praktisk & Priser',
            heroImage: data.heroImage || '',
            textBefore: data.textBefore || '',
            locationImage: data.locationImage || '',
            mapEmbedUrl: data.mapEmbedUrl || 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2249.484931924789!2d12.5903163159283!3d55.68268098053369!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x465253764030803b%3A0xf12e177be3965413!2sSankt%20Ann%C3%A6%20Plads%2012%2C%201250%20K%C3%B8benhavn!5e0!3m2!1sda!2sdk!4v1645541234567!5m2!1sda!2sdk',
            textAfter: data.textAfter || ''
          });
          
          if (data.heroImage) {
            setHeroPreview(data.heroImage);
          }
          
          if (data.locationImage) {
            setLocationPreview(data.locationImage);
          }
        }
      } catch (error) {
        console.error('Fejl ved hentning af praktisk/priser data:', error);
        setMessage({
          text: 'Der opstod en fejl ved indlæsning af indhold',
          type: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchPraktiskData();
    }
  }, [user]);

  // Initialisering af editorer
  useEffect(() => {
    if (textBeforeRef.current) {
      textBeforeRef.current.innerHTML = praktiskData.textBefore || '';
    }
    if (textAfterRef.current) {
      textAfterRef.current.innerHTML = praktiskData.textAfter || '';
    }
  }, [praktiskData.textBefore, praktiskData.textAfter]);

  // Handlere for hovedindhold
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPraktiskData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleHeroFileChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setHeroFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setHeroPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleLocationFileChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setLocationFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setLocationPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Tilføj event listener for selection change
  useEffect(() => {
    const handleSelectionChange = () => {
      // Find ud af hvilken editor der har fokus
      const activeElement = document.activeElement;
      const isTextBefore = textBeforeRef.current && textBeforeRef.current.contains(activeElement);
      const isTextAfter = textAfterRef.current && textAfterRef.current.contains(activeElement);
      
      if (isTextBefore) {
        checkActiveFormats('textBefore');
      } else if (isTextAfter) {
        checkActiveFormats('textAfter');
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, []);

  const handleEditorFocus = (editorType) => {
    const editor = {
      textBefore: textBeforeRef.current,
      textAfter: textAfterRef.current
    }[editorType];

    if (editor) {
      editor.focus();
      checkActiveFormats(editorType);
    }
  };

  const checkActiveFormats = (editorType) => {
    const setFormats = {
      textBefore: setTextBeforeFormats,
      textAfter: setTextAfterFormats
    }[editorType];
    
    setFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      heading: false,
      bullet: document.queryCommandState('insertUnorderedList'),
      link: document.queryCommandState('createLink'),
      underline: document.queryCommandState('underline')
    });
  };

  // Håndter link-indsættelse
  const handleLinkInsertion = (editor) => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    
    const range = selection.getRangeAt(0);
    const selectedText = range.toString().trim();
    
    // Tjek om selektionen er inden for editoren
    if (!editor || !editor.contains(range.commonAncestorContainer)) {
      return;
    }
    
    // Tjek om den valgte tekst er en URL
    const url = isValidUrl(selectedText) ? selectedText : prompt('Indtast URL:', 'https://');
    
    if (url && url.trim() !== '') {
      const linkText = selectedText || prompt('Indtast link tekst:', '');
      
      if (linkText && linkText.trim() !== '') {
        // Indsæt link direkte i HTML
        const linkElement = document.createElement('a');
        linkElement.href = url;
        linkElement.target = '_blank';
        linkElement.rel = 'noopener noreferrer';
        linkElement.textContent = linkText;
        
        // Indsæt linket
        range.deleteContents();
        range.insertNode(linkElement);
        
        // Flyt cursoren efter linket
        range.setStartAfter(linkElement);
        range.setEndAfter(linkElement);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  const handleFormatClick = (formatType, editorType) => {
    const editor = {
      textBefore: textBeforeRef.current,
      textAfter: textAfterRef.current
    }[editorType];

    if (!editor) return;
    
    // Fokuser på editoren hvis den ikke allerede har fokus
    if (!editor.contains(document.activeElement)) {
      editor.focus();
    }

    // Udfør formateringen direkte
    switch (formatType) {
      case 'bold':
        document.execCommand('bold', false);
        break;
      
      case 'italic':
        document.execCommand('italic', false);
        break;
      
      case 'heading':
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        
        const range = selection.getRangeAt(0);
        if (!range) return;
        
        const parentElement = range.commonAncestorContainer.parentElement;
        const isHeading = parentElement && parentElement.tagName === 'H2';
        
        if (isHeading) {
          document.execCommand('formatBlock', false, 'p');
        } else {
          document.execCommand('formatBlock', false, 'h2');
        }
        break;
      
      case 'bullet':
        document.execCommand('insertUnorderedList', false);
        break;
      
      case 'link':
        handleLinkInsertion(editor);
        break;
      
      case 'underline':
        document.execCommand('underline', false);
        break;
      
      default:
        break;
    }

    // Opdater aktive formateringer
    checkActiveFormats(editorType);
  };

  const isValidUrl = (text) => {
    try {
      if (text.match(/^(https?:\/\/|www\.)/i)) {
        const url = text.startsWith('www.') ? `https://${text}` : text;
        new URL(url);
        return url;
      }
      return false;
    } catch {
      return false;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      document.execCommand('insertLineBreak', false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      let heroImageUrl = praktiskData.heroImage;
      let locationImageUrl = praktiskData.locationImage;
      
      // Upload nyt hero billede hvis det er valgt
      if (heroFile) {
        const storageRef = ref(storage, `praktisk/hero-${Date.now()}`);
        await uploadBytes(storageRef, heroFile);
        heroImageUrl = await getDownloadURL(storageRef);
      }
      
      // Upload nyt lokationsbillede hvis det er valgt
      if (locationFile) {
        const storageRef = ref(storage, `praktisk/location-${Date.now()}`);
        await uploadBytes(storageRef, locationFile);
        locationImageUrl = await getDownloadURL(storageRef);
      }
      
      // Hent det formaterede indhold fra editoren og normaliser whitespace
      const textBefore = textBeforeRef.current ? normalizeWhitespace(textBeforeRef.current.innerHTML) : '';
      const textAfter = textAfterRef.current ? normalizeWhitespace(textAfterRef.current.innerHTML) : '';
      
      // Gem i Firestore
      await setDoc(doc(db, 'content', 'praktiskPriser'), {
        ...praktiskData,
        textBefore,
        textAfter,
        heroImage: heroImageUrl,
        locationImage: locationImageUrl,
        lastUpdated: new Date()
      });
      
      setPraktiskData(prev => ({
        ...prev,
        textBefore,
        textAfter,
        heroImage: heroImageUrl,
        locationImage: locationImageUrl
      }));
      
      setHeroFile(null);
      setLocationFile(null);
      setMessage({ type: 'success', text: 'Praktisk & Priser siden blev opdateret!' });
    } catch (error) {
      console.error("Fejl ved gem:", error);
      setMessage({ type: 'error', text: 'Fejl ved gem af Praktisk & Priser side.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin');
  };

  const closeMessage = () => {
    setMessage({ type: '', text: '' });
  };

  const goBack = () => {
    navigate('/admin');
  };

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Indlæser...</p>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <header className="admin-header">
        <div className="admin-logo">
          <img 
            src="/media/logo/psykologiskperspektiv-logo.avif" 
            alt="Psykologisk Perspektiv" 
            onClick={() => navigate('/admin')}
            style={{ cursor: 'pointer' }}
          />
        </div>
        <div className="admin-header-right">
          <div className="admin-user">
            <span>Logget ind som: </span>
            <strong>{user.email}</strong>
          </div>
          <button onClick={() => navigate('/admin')} className="back-button">
            ← Tilbage
          </button>
        </div>
      </header>
      
      <main className="admin-content">
        <div className="admin-praktisk-container">
          <button className="back-button" onClick={goBack}>
            ← Tilbage til Admin
          </button>
          
          <h1>Administrer Praktisk & Priser</h1>
          
          {message.text && (
            <div className={`admin-message ${message.type}`}>
              <p>{message.text}</p>
              <button 
                className="close-message" 
                onClick={closeMessage}
              >
                ×
              </button>
            </div>
          )}
          
          <div className="admin-section">
            <div className="admin-section-header">
              <h2>Indhold på Praktisk & Priser siden</h2>
            </div>
            
            {isEditing && (
              <div className="praktisk-form">
                <div className="form-group">
                  <label htmlFor="title">Overskrift</label>
                  <input 
                    type="text" 
                    id="title" 
                    name="title"
                    value={praktiskData.title || ''}
                    onChange={handleInputChange}
                  />
                </div>
                
                <div className="image-uploader">
                  <label>Hovedbillede</label>
                  
                  {heroPreview && (
                    <div className="image-preview hero-preview">
                      <img src={heroPreview} alt="Forhåndsvisning" />
                    </div>
                  )}
                  
                  <label className="upload-btn">
                    {heroPreview ? 'Skift billede' : 'Vælg billede'}
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleHeroFileChange}
                      id="hero-image-upload"
                    />
                  </label>
                </div>
                
                <div className="form-group">
                  <label htmlFor="textBefore">Tekst før kort og map</label>
                  <FormatBar 
                    activeFormats={textBeforeFormats}
                    onFormatClick={(format) => handleFormatClick(format, 'textBefore')}
                  />
                  <div
                    ref={textBeforeRef}
                    className="rich-text-editor"
                    contentEditable
                    onKeyDown={handleKeyDown}
                    onFocus={() => handleEditorFocus('textBefore')}
                    data-placeholder="Skriv teksten der skal vises før kort og map..."
                  />
                </div>
                
                <div className="image-uploader">
                  <label>Lokationsbillede</label>
                  
                  {locationPreview && (
                    <div className="image-preview">
                      <img src={locationPreview} alt="Forhåndsvisning" />
                    </div>
                  )}
                  
                  <label className="upload-btn">
                    {locationPreview ? 'Skift billede' : 'Vælg billede'}
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleLocationFileChange}
                      id="location-image-upload"
                    />
                  </label>
                </div>
                
                <div className="form-group">
                  <label htmlFor="mapEmbedUrl">Google Maps Embed URL</label>
                  <input 
                    type="text" 
                    id="mapEmbedUrl" 
                    name="mapEmbedUrl"
                    value={praktiskData.mapEmbedUrl || ''}
                    onChange={handleInputChange}
                  />
                  <span className="form-help-text">
                    Find din lokation på Google Maps, klik på "Del", vælg "Integrer kort" og kopier URL'en der er inde i src="..."
                  </span>
                </div>
                
                <div className="form-group">
                  <label htmlFor="textAfter">Tekst efter kort og map</label>
                  <FormatBar 
                    activeFormats={textAfterFormats}
                    onFormatClick={(format) => handleFormatClick(format, 'textAfter')}
                  />
                  <div
                    ref={textAfterRef}
                    className="rich-text-editor"
                    contentEditable
                    onKeyDown={handleKeyDown}
                    onFocus={() => handleEditorFocus('textAfter')}
                    data-placeholder="Skriv teksten der skal vises efter kort og map..."
                  />
                </div>
                
                <div className="form-actions">
                  <button 
                    className="save-btn"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Gemmer...' : 'Gem ændringer'}
                  </button>
                  <button 
                    className="cancel-btn"
                    onClick={handleCancel}
                    disabled={isSaving}
                  >
                    Annuller
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      
      <footer className="admin-footer">
        <p>&copy; {new Date().getFullYear()} Psykologisk Perspektiv - Admin Panel</p>
      </footer>
    </div>
  );
};

export default AdminPraktiskPriser;
